
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Student, Family, Parent } from '@/types/models';
import { Button } from '@/components/ui/button';
import { Separator } from "@/components/ui/separator";
import { mapToInviteStatus } from '@/utils/typeUtils';

import FamilyDetailsSidebar from '@/components/families/FamilyDetailsSidebar';
import EditStudentDialog from '@/components/students/EditStudentDialog';
import StudentInformationCard from '@/components/students/cards/StudentInformationCard';
import StudentEnrollmentsCard from '@/components/students/cards/StudentEnrollmentsCard';
import ConnectToFamilyDialog from '@/components/students/ConnectToFamilyDialog';

const StudentDetail: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const [student, setStudent] = useState<Student | null>(null);
  const [studentFamily, setStudentFamily] = useState<Family | null>(null);
  const [familyParents, setFamilyParents] = useState([]);
  const [familyStudents, setFamilyStudents] = useState([]);
  const [isFamilySidebarOpen, setIsFamilySidebarOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isConnectToFamilyDialogOpen, setIsConnectToFamilyDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStudentData();
  }, [studentId]);

  const loadStudentData = async () => {
    setIsLoading(true);
    setError(null);
    
    if (!studentId) {
      setError("Student ID is missing.");
      setIsLoading(false);
      toast.error("Student ID is missing.");
      return;
    }

    try {
      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select('*')
        .eq('id', studentId)
        .single();

      if (studentError) {
        throw studentError;
      }

      if (!studentData) {
        setError("Student not found.");
        setIsLoading(false);
        toast.error("Student not found.");
        return;
      }

      setStudent(studentData as Student);

      if (studentData.family_id) {
        const { data: familyData, error: familyError } = await supabase
          .from('families')
          .select('*')
          .eq('id', studentData.family_id)
          .single();

        if (familyError) {
          throw familyError;
        }

        setStudentFamily(familyData as Family);
        
        const { data: parentsData } = await supabase
          .from('parents')
          .select('*')
          .eq('family_id', familyData.id);
          
        const typedParents: Parent[] = (parentsData || []).map(parent => ({
          ...parent,
          invite_status: mapToInviteStatus(parent.invite_status)
        }));
        
        setFamilyParents(typedParents);
        
        const { data: studentsData } = await supabase
          .from('students')
          .select('*')
          .eq('family_id', familyData.id);
          
        setFamilyStudents(studentsData || []);
      }
      
      setIsLoading(false);
    } catch (error: any) {
      console.error("Error loading student data:", error);
      setError(error.message || "Failed to load student data.");
      toast.error(error.message || "Failed to load student data.");
      setIsLoading(false);
    }
  };

  const handleConnectToFamily = () => {
    setIsConnectToFamilyDialogOpen(true);
  };

  const handleStudentConnected = (updatedStudent: Student) => {
    setStudent(updatedStudent);
    loadStudentData(); // Reload to get family data
  };

  if (isLoading) {
    return <div className="text-center text-muted-foreground py-12">Loading student data...</div>;
  }

  if (error || !student) {
    return (
      <div className="container mx-auto py-10">
        <div className="mb-4">
          <h1 className="text-3xl font-bold tracking-tight">Student Details</h1>
        </div>
        <div className="bg-background p-6 rounded-lg border">
          <p className="text-red-500">{error || "Failed to load student"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10">
      <div className="mb-4 flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Student Details</h1>
        <Button size="sm" onClick={() => setIsEditDialogOpen(true)}>
          <Pencil className="mr-2 h-4 w-4" />
          Edit Student
        </Button>
      </div>
      <p className="text-muted-foreground">
        View and manage student information and enrollments.
      </p>
      <Separator className="my-4" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="md:col-span-3">
          <StudentInformationCard
            student={student}
            studentFamily={studentFamily}
            onViewFamily={() => setIsFamilySidebarOpen(true)}
            onConnectToFamily={handleConnectToFamily}
          />
          <StudentEnrollmentsCard studentId={studentId} />
        </div>

        {studentFamily && (
          <FamilyDetailsSidebar 
            family={studentFamily}
            parents={familyParents}
            students={familyStudents}
            isOpen={isFamilySidebarOpen}
            onClose={() => setIsFamilySidebarOpen(false)}
            onSendInvite={() => {}}
            onEditParent={() => {}}
            onAddStudent={() => {}}
            onAddParent={() => {}}
          />
        )}

        <EditStudentDialog 
          open={isEditDialogOpen} 
          onOpenChange={setIsEditDialogOpen} 
          student={student}
          onUpdated={(updatedStudent) => {
            setStudent(updatedStudent);
            loadStudentData();
          }} 
        />

        <ConnectToFamilyDialog
          open={isConnectToFamilyDialogOpen}
          onOpenChange={setIsConnectToFamilyDialogOpen}
          student={student}
          onStudentConnected={handleStudentConnected}
        />
      </div>
    </div>
  );
};

export default StudentDetail;
